/**
 * Generated by orval v7.17.0 🍺
 * Do not edit manually.
 * FieldPay API
 * FieldPay API for managing organizations, opportunities, payouts and talent management
 * OpenAPI spec version: v1
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import * as axios from "axios";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import type { AssignPayoutPolicyDto } from "../types/assignPayoutPolicyDto";

import type { ListTalentMembersParams } from "../types/listTalentMembersParams";

import type { PayeePayoutTermsDto } from "../types/payeePayoutTermsDto";

import type { ProblemDetails } from "../types/problemDetails";

import type { SetTalentMemberPushToCardDto } from "../types/setTalentMemberPushToCardDto";

import type { TalentMemberDto } from "../types/talentMemberDto";

import type { TalentMemberDtoPaginatedResult } from "../types/talentMemberDtoPaginatedResult";

import type { UpdateTalentMemberDto } from "../types/updateTalentMemberDto";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * **Required Permission(s):** ORG_PAYEE_GET
 */
export const getTalentMemberById = (
  id: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<TalentMemberDto>> => {
  return axios.default.get(`/api/talent-members/${id}`, options);
};

export const getGetTalentMemberByIdQueryKey = (id?: string) => {
  return [`/api/talent-members/${id}`] as const;
};

export const getGetTalentMemberByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getTalentMemberById>>,
  TError = AxiosError<void | ProblemDetails>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTalentMemberById>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTalentMemberByIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTalentMemberById>>
  > = ({ signal }) => getTalentMemberById(id, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getTalentMemberById>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetTalentMemberByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTalentMemberById>>
>;
export type GetTalentMemberByIdQueryError = AxiosError<void | ProblemDetails>;

export function useGetTalentMemberById<
  TData = Awaited<ReturnType<typeof getTalentMemberById>>,
  TError = AxiosError<void | ProblemDetails>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTalentMemberById>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetTalentMemberByIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * **Required Permission(s):** ORG_PAYEE_UPDATE
 */
export const updateTalentMember = (
  id: string,
  updateTalentMemberDto: UpdateTalentMemberDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<TalentMemberDto>> => {
  return axios.default.put(
    `/api/talent-members/${id}`,
    updateTalentMemberDto,
    options,
  );
};

export const getUpdateTalentMemberMutationOptions = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTalentMember>>,
    TError,
    { id: string; data: UpdateTalentMemberDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateTalentMember>>,
  TError,
  { id: string; data: UpdateTalentMemberDto },
  TContext
> => {
  const mutationKey = ["updateTalentMember"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateTalentMember>>,
    { id: string; data: UpdateTalentMemberDto }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateTalentMember(id, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTalentMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateTalentMember>>
>;
export type UpdateTalentMemberMutationBody = UpdateTalentMemberDto;
export type UpdateTalentMemberMutationError = AxiosError<ProblemDetails | void>;

export const useUpdateTalentMember = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateTalentMember>>,
    TError,
    { id: string; data: UpdateTalentMemberDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateTalentMember>>,
  TError,
  { id: string; data: UpdateTalentMemberDto },
  TContext
> => {
  const mutationOptions = getUpdateTalentMemberMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_PAYEE_DELETE
 */
export const deleteTalentMember = (
  id: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.default.delete(`/api/talent-members/${id}`, options);
};

export const getDeleteTalentMemberMutationOptions = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTalentMember>>,
    TError,
    { id: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteTalentMember>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["deleteTalentMember"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteTalentMember>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteTalentMember(id, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTalentMemberMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteTalentMember>>
>;

export type DeleteTalentMemberMutationError = AxiosError<void | ProblemDetails>;

export const useDeleteTalentMember = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteTalentMember>>,
    TError,
    { id: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteTalentMember>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getDeleteTalentMemberMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_PAYEE_LIST
 */
export const listTalentMembers = (
  params?: ListTalentMembersParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<TalentMemberDtoPaginatedResult>> => {
  return axios.default.get(`/api/talent-members`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getListTalentMembersQueryKey = (
  params?: ListTalentMembersParams,
) => {
  return [`/api/talent-members`, ...(params ? [params] : [])] as const;
};

export const getListTalentMembersQueryOptions = <
  TData = Awaited<ReturnType<typeof listTalentMembers>>,
  TError = AxiosError<void>,
>(
  params?: ListTalentMembersParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listTalentMembers>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListTalentMembersQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listTalentMembers>>
  > = ({ signal }) => listTalentMembers(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listTalentMembers>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListTalentMembersQueryResult = NonNullable<
  Awaited<ReturnType<typeof listTalentMembers>>
>;
export type ListTalentMembersQueryError = AxiosError<void>;

export function useListTalentMembers<
  TData = Awaited<ReturnType<typeof listTalentMembers>>,
  TError = AxiosError<void>,
>(
  params?: ListTalentMembersParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listTalentMembers>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListTalentMembersQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * **Required Permission(s):** ORG_TALENT_SET_PUSH_TO_CARD
 */
export const setTalentMemberPushToCard = (
  id: string,
  setTalentMemberPushToCardDto: SetTalentMemberPushToCardDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<TalentMemberDto>> => {
  return axios.default.put(
    `/api/talent-members/${id}/push-to-card`,
    setTalentMemberPushToCardDto,
    options,
  );
};

export const getSetTalentMemberPushToCardMutationOptions = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setTalentMemberPushToCard>>,
    TError,
    { id: string; data: SetTalentMemberPushToCardDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setTalentMemberPushToCard>>,
  TError,
  { id: string; data: SetTalentMemberPushToCardDto },
  TContext
> => {
  const mutationKey = ["setTalentMemberPushToCard"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setTalentMemberPushToCard>>,
    { id: string; data: SetTalentMemberPushToCardDto }
  > = (props) => {
    const { id, data } = props ?? {};

    return setTalentMemberPushToCard(id, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SetTalentMemberPushToCardMutationResult = NonNullable<
  Awaited<ReturnType<typeof setTalentMemberPushToCard>>
>;
export type SetTalentMemberPushToCardMutationBody =
  SetTalentMemberPushToCardDto;
export type SetTalentMemberPushToCardMutationError =
  AxiosError<ProblemDetails | void>;

export const useSetTalentMemberPushToCard = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setTalentMemberPushToCard>>,
    TError,
    { id: string; data: SetTalentMemberPushToCardDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof setTalentMemberPushToCard>>,
  TError,
  { id: string; data: SetTalentMemberPushToCardDto },
  TContext
> => {
  const mutationOptions = getSetTalentMemberPushToCardMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_PAYEE_GET
 */
export const getTalentMemberPayoutTerms = (
  id: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PayeePayoutTermsDto>> => {
  return axios.default.get(`/api/talent-members/${id}/payout-terms`, options);
};

export const getGetTalentMemberPayoutTermsQueryKey = (id?: string) => {
  return [`/api/talent-members/${id}/payout-terms`] as const;
};

export const getGetTalentMemberPayoutTermsQueryOptions = <
  TData = Awaited<ReturnType<typeof getTalentMemberPayoutTerms>>,
  TError = AxiosError<void | ProblemDetails>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTalentMemberPayoutTerms>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTalentMemberPayoutTermsQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getTalentMemberPayoutTerms>>
  > = ({ signal }) =>
    getTalentMemberPayoutTerms(id, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getTalentMemberPayoutTerms>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetTalentMemberPayoutTermsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getTalentMemberPayoutTerms>>
>;
export type GetTalentMemberPayoutTermsQueryError =
  AxiosError<void | ProblemDetails>;

export function useGetTalentMemberPayoutTerms<
  TData = Awaited<ReturnType<typeof getTalentMemberPayoutTerms>>,
  TError = AxiosError<void | ProblemDetails>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getTalentMemberPayoutTerms>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetTalentMemberPayoutTermsQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * **Required Permission(s):** ORG_PAYEE_ASSIGN_PAYOUT_POLICY
 */
export const assignTalentMemberPayoutPolicy = (
  id: string,
  assignPayoutPolicyDto: AssignPayoutPolicyDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PayeePayoutTermsDto>> => {
  return axios.default.post(
    `/api/talent-members/${id}/assign-payout-policy`,
    assignPayoutPolicyDto,
    options,
  );
};

export const getAssignTalentMemberPayoutPolicyMutationOptions = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof assignTalentMemberPayoutPolicy>>,
    TError,
    { id: string; data: AssignPayoutPolicyDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof assignTalentMemberPayoutPolicy>>,
  TError,
  { id: string; data: AssignPayoutPolicyDto },
  TContext
> => {
  const mutationKey = ["assignTalentMemberPayoutPolicy"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof assignTalentMemberPayoutPolicy>>,
    { id: string; data: AssignPayoutPolicyDto }
  > = (props) => {
    const { id, data } = props ?? {};

    return assignTalentMemberPayoutPolicy(id, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AssignTalentMemberPayoutPolicyMutationResult = NonNullable<
  Awaited<ReturnType<typeof assignTalentMemberPayoutPolicy>>
>;
export type AssignTalentMemberPayoutPolicyMutationBody = AssignPayoutPolicyDto;
export type AssignTalentMemberPayoutPolicyMutationError =
  AxiosError<ProblemDetails | void>;

export const useAssignTalentMemberPayoutPolicy = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof assignTalentMemberPayoutPolicy>>,
    TError,
    { id: string; data: AssignPayoutPolicyDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof assignTalentMemberPayoutPolicy>>,
  TError,
  { id: string; data: AssignPayoutPolicyDto },
  TContext
> => {
  const mutationOptions =
    getAssignTalentMemberPayoutPolicyMutationOptions(options);

  return useMutation(mutationOptions);
};
